import React, { useState, useEffect, useMemo, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { 
  Modal, Form, Input, Button, Select, Typography, notification, 
  Empty, Spin, Switch, Tag, Space, Checkbox,
  Input as AntInput, List, Popconfirm, Alert,
  Drawer, Descriptions, Divider, Pagination
} from 'antd';
import { 
  UserOutlined, PlusOutlined, ReloadOutlined, SearchOutlined,
  CheckCircleOutlined, CloseCircleOutlined, LockOutlined,
  TeamOutlined, UnorderedListOutlined, EditOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import UserPermissionGrants from './UserPermissionGrants';
import { useAuth } from '../../assets/utils/useAuth';
import { usePermissions } from '../../assets/utils/usePermissions';
import './staff-dashboard.css';
import lib from "../../assets/utils/lib";
import DashboardHero from '../dashboard/components/DashboardHero';
import DashboardSection from '../dashboard/components/DashboardSection';
import { ADMIN_REVIEW_MODAL_CLASS, hasMetricValue } from '../dashboard/utils';
import StaffListingCard from './StaffListingCard';
import StaffProfileEditor from './StaffProfileEditor';
import {
  fetchStaffUsers,
  createStaffUser,
  fetchStaffRoles,
  fetchPermissions,
  fetchCurrentUserProfile,
  assignRoleToStaff,
  removeRoleFromStaff,
  createRole,
  assignPermissionsToRole,
  updateStaffUser,
  changeStaffPassword,
} from './rbacApi';

const { Text } = Typography;

// Role expiry is sent as a duration, not a date. expires_at is compared against
// SQL NOW() on the server, so a timestamp built in the browser is wrong by
// whatever the browser and server disagree on — here PHP runs UTC while the
// server reports WAT, which made "in one hour" already expired. The backend
// resolves these with DATE_ADD(NOW(), ...).
const ROLE_EXPIRY_OPTIONS = [
  { value: 0, label: 'No expiry' },
  { value: 60, label: '1 hour' },
  { value: 60 * 8, label: '8 hours' },
  { value: 60 * 24, label: '1 day' },
  { value: 60 * 24 * 7, label: '1 week' },
  { value: 60 * 24 * 30, label: '30 days' },
  { value: 60 * 24 * 90, label: '90 days' },
];

const StaffManagement = props => {
  const { user, refreshProfile } = useAuth();
  const { canStayOnModule } = usePermissions();
  const canManageStaff = canStayOnModule('staffManagement', [1, 6, 7]);
  const [staffMembers, setStaffMembers] = useState([]);
  const [staffPage, setStaffPage] = useState(1);
  const [staffPageSize, setStaffPageSize] = useState(10);
  const [loading, setLoading] = useState(false);
  const navigator = useNavigate();
  
  // Modal states
  const [createModalVisible, setCreateModalVisible] = useState(false);
  const [roleModalVisible, setRoleModalVisible] = useState(false);
  const [roleCreationModalVisible, setRoleCreationModalVisible] = useState(false);
  const [roleModalLoading, setRoleModalLoading] = useState(false);
  const [profileDrawerVisible, setProfileDrawerVisible] = useState(false);
  const [selectedProfile, setSelectedProfile] = useState(null);
  const [profileEditing, setProfileEditing] = useState(false);
  const [profileSaving, setProfileSaving] = useState(false);
  
  // Form instances
  const [createForm] = Form.useForm();
  const [roleForm] = Form.useForm();
  const [newRoleForm] = Form.useForm();
  
  // Search and filter states
  const [searchText, setSearchText] = useState('');
  const [filterRole, setFilterRole] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [filterDepartment, setFilterDepartment] = useState('');
  const clearFilters = useCallback(() => {
    setSearchText('');
    setFilterRole('');
    setFilterStatus('');
    setFilterDepartment('');
  }, []);
  
  // Data sources
  const [rbacRoles, setRbacRoles] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [permissions, setPermissions] = useState([]);

  const [viewerProfile, setViewerProfile] = useState(null);
  const [canManageRoles, setCanManageRoles] = useState(false);

  const [selectedStaff, setSelectedStaff] = useState(null);
  const [roleAssignments, setRoleAssignments] = useState([]);
  
  useEffect(() => {
    if (!user) {
      navigator('/login');
      return;
    }
    if (!canManageStaff) {
      notification.warning({
        message: 'Access Denied',
        description: "You don't have permission to manage staff.",
        placement: 'topLeft',
      });
      navigator('/dashboard');
    }
  }, [user, canManageStaff, navigator]);

  useEffect(() => {
    if (!user || !canManageStaff) return;
    loadStaff();
    loadDepartments();
    loadRbacRoles();
    loadViewerProfile();
    loadPermissions();
  }, [user, canManageStaff]); // eslint-disable-line react-hooks/exhaustive-deps

  const normalizeStaffMember = (record) => {
    if (!record) return null;
    // Backend now returns rbac_roles when include_roles=true
    const rolesArray = record.rbac_roles || record.roles || record.assigned_roles || [];
    const roleIds = rolesArray.map(role => String(role.id ?? role.role_id ?? role.uuid ?? role.name));
    const primaryRoleName = rolesArray[0]?.display_name || rolesArray[0]?.name || rolesArray[0]?.role_name || record.role?.staffRoleName;
    const isPlatformOperator = Boolean(
      record.is_platform_operator
      || rolesArray.some((role) => String(role?.name || role?.role_name || '').toLowerCase() === 'platform_operator')
    );

    return {
      id: record.id ?? record.user_id ?? record.staff_id,
      staffId: record.staff_id || record.staffId || record.username || record.email || 'N/A',
      fullName: String(
        record.full_name || record.fullName || record.name || record.display_name || ''
      ).trim(),
      status: Number(record.status ?? record.is_active ?? record.enabled ?? record.active ?? 0),
      department: record.department || record.department_info || record.department_details || record.departmentId ? { name: record.department?.name || record.department_name, id: record.department?.id || record.departmentId } : (record.department ? { name: record.department } : null),
      roles: rolesArray,
      roleIds,
      primaryRoleName: primaryRoleName || 'Unassigned',
      email: record.email || record.contact_email,
      phone: record.phone || record.contact_phone || '',
      isPlatformOperator,
      is_platform_operator: isPlatformOperator,
      raw: record,
    };
  };

  const loadStaff = async () => {
    setLoading(true);
    let staffList = [];
    try {
      // The listing endpoint pages at 100 rows (defaulting to 20 when unasked);
      // walk every page so search and the filters cover the whole roster.
      const response = await fetchStaffUsers({ page: 1, limit: 100 });
      const payload = response?.data || response?.users || response?.results || response;

      if (payload?.error) {
        notification.error({
          message: payload?.message || 'Failed to load staff members',
          placement: 'topLeft'
        });
      } else {
        let records = payload?.data || payload?.items || payload?.results || payload || [];
        records = Array.isArray(records) ? records : [];
        const totalPages = Number(response?.pagination?.pages || payload?.pagination?.pages) || 1;
        const maxPages = Math.min(totalPages, 20);
        for (let page = 2; page <= maxPages; page += 1) {
          const nextResponse = await fetchStaffUsers({ page, limit: 100 });
          const nextPayload = nextResponse?.data || nextResponse?.users || nextResponse?.results || nextResponse;
          const nextRecords = nextPayload?.data || nextPayload?.items || nextPayload?.results || nextPayload || [];
          if (!Array.isArray(nextRecords) || nextRecords.length === 0) break;
          records = records.concat(nextRecords);
        }
        staffList = records.map(normalizeStaffMember).filter(Boolean);
        setStaffMembers(staffList);
      }
    } catch (error) {
      notification.error({ 
        message: 'Error loading staff members',
        placement: 'topLeft'
      });
      console.error('Error loading staff:', error);
    } finally {
      setLoading(false);
    }
    return staffList;
  };

  const loadDepartments = async () => {
    try {
      const data = await lib.departments();
      if (data?.data?.data) {
        setDepartments(data.data.data.map(d => ({ value: d.id, label: d.name })));
      }
    } catch (error) {
      console.error('Error loading departments:', error);
    }
  };

  const loadRbacRoles = async () => {
    try {
      const response = await fetchStaffRoles();
      const payload = response?.data || response?.roles || response;
      const records = payload?.data || payload?.items || payload?.results || payload || [];
      setRbacRoles(Array.isArray(records) ? records : []);
    } catch (error) {
      console.error('Error loading RBAC roles:', error);
      notification.error({
        message: 'Unable to load RBAC roles',
        placement: 'topLeft',
      });
    }
  };

  const loadPermissions = async () => {
    try {
      const response = await fetchPermissions();
      const payload = response?.data || response?.permissions || response;
      const records = payload?.data || payload?.items || payload?.results || payload || [];
      setPermissions(Array.isArray(records) ? records : []);
    } catch (error) {
      console.error('Error loading permissions:', error);
    }
  };

  const loadViewerProfile = async () => {
    try {
      const response = await fetchCurrentUserProfile();
      const profile = response?.data || response?.profile || response;
      setViewerProfile(profile);
      const perms = profile?.permissions || profile?.permission_flags || profile?.permissionCodes || [];
      let hasAdminPermission = false;

      if (Array.isArray(perms)) {
        hasAdminPermission = perms.includes('admin.role_management') || perms.includes('admin.*');
      } else if (perms && typeof perms === 'object') {
        hasAdminPermission = Boolean(
          perms['admin.role_management'] ||
          perms['admin.*'] ||
          perms.admin?.role_management ||
          perms.role_management ||
          perms.admin_role_management
        );
      }

      // Fallback: If no RBAC permissions but user has legacy super admin role or is_admin flag
      // This handles the migration period where RBAC roles haven't been assigned yet
      if (!hasAdminPermission) {
        const jwtData = user?.jwt ? JSON.parse(atob(user.jwt.split('.')[1])) : null;
        const isLegacySuperAdmin = canStayOnModule('rbacAdmin', [1]);
        const isAdminFromJWT = jwtData?.data?.rbac_context?.is_admin === true;
        
        if (isLegacySuperAdmin || isAdminFromJWT) {
          hasAdminPermission = true;
          console.warn('Using legacy admin check - RBAC roles not yet assigned. Please run seeder.');
        }
      }

      setCanManageRoles(hasAdminPermission);
    } catch (error) {
      console.error('Error loading viewer profile:', error);
      setCanManageRoles(false);
    }
  };

  const handleCreateStaff = async (values) => {
    setLoading(true);
    try {
      const payload = {
        email: String(values.email || '').trim().toLowerCase(),
        password: values.password,
        department_id: values.departmentId,
        send_invite: values.send_invite !== false,
        // The backend derives the legacy staff.roleId from this, so one role
        // picker is enough.
        rbac_role_ids: [Number(values.rbacRoleId)],
      };
      if (values.full_name?.trim() || values.fullName?.trim() || values.name?.trim()) {
        payload.full_name = String(values.full_name || values.fullName || values.name || '').trim();
      }
      if (values.staffId?.trim()) payload.staff_id = values.staffId.trim();

      const body = await createStaffUser(payload);
      if (Number(body?.error) === 0) {
        notification.success({
          message: body?.message || 'Staff member created successfully',
          placement: 'topLeft'
        });
        setCreateModalVisible(false);
        createForm.resetFields();
        await loadStaff();
      } else {
        notification.error({
          message: body?.message || 'Failed to create staff member',
          placement: 'topLeft'
        });
      }
    } catch (error) {
      notification.error({
        message: error?.response?.data?.message || 'Error creating staff member',
        placement: 'topLeft'
      });
      console.error('Error creating staff:', error);
    } finally {
      setLoading(false);
    }
  };

  const refreshSelectedStaff = async (staffId) => {
    const updatedList = await loadStaff();
    if (!staffId) return;
    const refreshed = updatedList?.find(record => record.id === staffId);
    if (refreshed) {
      setSelectedStaff(refreshed);
      setRoleAssignments(refreshed.roles || []);
    }
  };

  const handleToggleStatus = async (staffId, checked) => {
    const target = staffMembers.find((s) => Number(s.id) === Number(staffId));
    if (target?.isPlatformOperator || target?.is_platform_operator) {
      notification.warning({
        message: 'Platform operators can only be enabled/disabled via CLI',
        placement: 'topLeft',
        duration: 4,
      });
      return;
    }

    const payload = { id: staffId, payload: checked ? 1 : 0 };
    setLoading(true);
    
    notification.info({
      message: 'Updating Status',
      key: 'updating',
      duration: 2,
      placement: 'topLeft'
    });

    try {
      const data = await lib.updateProfile(payload);
      if (data?.data?.error === 0) {
        notification.success({
          message: data?.data?.message || 'Status updated successfully',
          key: 'updating',
          placement: 'topLeft',
          duration: 2
        });
        await loadStaff();
      } else {
        notification.error({
          message: data?.data?.message || 'Failed to update status',
          key: 'updating',
          placement: 'topLeft',
          duration: 2
        });
      }
    } catch (error) {
      const apiMessage =
        error?.response?.data?.message
        || error?.response?.data?.data?.message
        || error?.message;
      notification.error({
        message: apiMessage || 'Error updating status',
        key: 'updating',
        placement: 'topLeft',
        duration: 3
      });
      console.error('Error updating status:', error);
    } finally {
      setLoading(false);
    }
  };

  const openRoleModal = (staff) => {
    setSelectedStaff(staff);
    setRoleAssignments(staff.roles || []);
    roleForm.resetFields();
    setRoleModalVisible(true);
  };

  const closeRoleModal = () => {
    setRoleModalVisible(false);
    setSelectedStaff(null);
    setRoleAssignments([]);
    roleForm.resetFields();
  };

  const openProfileDrawer = useCallback((staff) => {
    // Ensure we're using the normalized staff object
    if (!staff) {
      return;
    }
    // Set the selected profile before opening drawer to ensure correct data is shown
    setSelectedProfile({ ...staff }); // Create a new object to ensure React detects the change
    setProfileEditing(false);
    setProfileDrawerVisible(true);
  }, []);

  const closeProfileDrawer = () => {
    setProfileDrawerVisible(false);
    setSelectedProfile(null);
    setProfileEditing(false);
  };

  const handleSaveStaffProfile = async (values) => {
    if (!selectedProfile?.id) return;
    setProfileSaving(true);
    try {
      const payload = {
        email: String(values.email || '').trim().toLowerCase(),
        full_name: String(values.full_name || values.fullName || values.name || '').trim(),
        phone: String(values.phone || '').trim(),
      };
      const result = await updateStaffUser(selectedProfile.id, payload);
      if (result?.error) {
        notification.error({
          message: result?.message || 'Failed to update staff profile',
          placement: 'topLeft',
        });
        return;
      }

      const newPassword = String(values.password || '');
      if (newPassword) {
        const passwordResult = await changeStaffPassword(selectedProfile.id, {
          password: newPassword,
          confirm_password: String(values.confirm_password || ''),
        });
        if (passwordResult?.error) {
          notification.error({
            message: passwordResult?.message || 'Profile saved, but password was not changed',
            placement: 'topLeft',
          });
          return;
        }
      }

      notification.success({
        message: newPassword ? 'Staff profile and password updated' : (result?.message || 'Staff profile updated'),
        placement: 'topLeft',
      });
      setProfileEditing(false);
      const updatedList = await loadStaff();
      const refreshed = updatedList?.find(
        (record) => Number(record.id) === Number(selectedProfile.id)
      );
      const fromUpdate = result?.data && typeof result.data === 'object'
        ? normalizeStaffMember({ ...(selectedProfile.raw || {}), ...result.data, rbac_roles: selectedProfile.roles })
        : null;
      const nextProfile = (refreshed?.fullName ? refreshed : null) || fromUpdate || refreshed;
      if (nextProfile) {
        setSelectedProfile({ ...nextProfile, roles: nextProfile.roles?.length ? nextProfile.roles : (selectedProfile.roles || []) });
      }
      if (
        Number(selectedProfile.id) === Number(user?.id)
        || Number(selectedProfile.id) === Number(user?.rbac_user_id)
        || Number(selectedProfile.id) === Number(user?.profile?.user?.id)
      ) {
        try {
          await refreshProfile();
        } catch (error) {
          console.error('Failed to refresh own profile after admin edit:', error);
        }
      }
    } catch (error) {
      notification.error({
        message:
          error?.response?.data?.message
          || error?.message
          || 'Failed to update staff profile',
        placement: 'topLeft',
      });
    } finally {
      setProfileSaving(false);
    }
  };

  const handleAssignRole = async (values) => {
    if (!selectedStaff) return;
    setRoleModalLoading(true);
    try {
      const payload = {
        role_id: values.role_id,
      };

      // Relative, so the server resolves it on its own clock.
      if (values.expires_in_minutes) {
        payload.expires_in_minutes = values.expires_in_minutes;
      }

      if (values.context_note) {
        payload.context_data = { note: values.context_note };
      }

      await assignRoleToStaff(selectedStaff.id, payload);
      notification.success({
        message: 'Role assigned successfully',
        placement: 'topLeft',
      });
      roleForm.resetFields();
      await refreshSelectedStaff(selectedStaff.id);
      
      // If the updated staff is the current user, refresh their profile
      if (selectedStaff.id === user?.id || selectedStaff.id === user?.rbac_user_id) {
        try {
          await refreshProfile();
          notification.info({
            message: 'Permissions Updated',
            description: 'Your permissions have been refreshed.',
            placement: 'topRight',
            duration: 3
          });
        } catch (error) {
          console.error('Failed to refresh profile:', error);
        }
      }
    } catch (error) {
      notification.error({
        message: 'Failed to assign role',
        description: error?.response?.data?.message || error?.message,
        placement: 'topLeft',
      });
    } finally {
      setRoleModalLoading(false);
    }
  };

  const handleRemoveRole = async (role) => {
    if (!selectedStaff) return;
    const roleId = role?.id ?? role?.role_id;
    if (!roleId) return;
    setRoleModalLoading(true);
    try {
      await removeRoleFromStaff(selectedStaff.id, roleId);
      notification.success({
        message: 'Role removed successfully',
        placement: 'topLeft',
      });
      await refreshSelectedStaff(selectedStaff.id);
      
      // If the updated staff is the current user, refresh their profile
      if (selectedStaff.id === user?.id || selectedStaff.id === user?.rbac_user_id) {
        try {
          await refreshProfile();
          notification.info({
            message: 'Permissions Updated',
            description: 'Your permissions have been refreshed.',
            placement: 'topRight',
            duration: 3
          });
        } catch (error) {
          console.error('Failed to refresh profile:', error);
        }
      }
    } catch (error) {
      notification.error({
        message: 'Failed to remove role',
        description: error?.response?.data?.message || error?.message,
        placement: 'topLeft',
      });
    } finally {
      setRoleModalLoading(false);
    }
  };

  const handleCreateRole = async (values) => {
    setRoleModalLoading(true);
    try {
      const payload = {
        name: values.name,
        display_name: values.display_name,
        description: values.description,
        user_type: 'staff',
      };
      if (values.department_id !== undefined && values.department_id !== null && values.department_id !== '') {
        payload.department_id = Number(values.department_id);
      }
      const roleResponse = await createRole(payload);
      const newRole = roleResponse?.data || roleResponse?.role;

      if (values.permission_ids?.length) {
        await assignPermissionsToRole(newRole?.id ?? newRole?.role_id, {
          permission_ids: values.permission_ids,
        });
      }

      notification.success({
        message: 'Role created successfully',
        placement: 'topLeft',
      });
      newRoleForm.resetFields();
      setRoleCreationModalVisible(false);
      await loadRbacRoles();
      if (selectedStaff) {
        await refreshSelectedStaff(selectedStaff.id);
      }
    } catch (error) {
      notification.error({
        message: 'Failed to create role',
        description: error?.response?.data?.message || error?.message,
        placement: 'topLeft',
      });
    } finally {
      setRoleModalLoading(false);
    }
  };

  // Filtered data
  const filteredData = useMemo(() => staffMembers.filter(staff => {
    const q = searchText.toLowerCase();
    const matchesSearch = !searchText || 
      staff.staffId?.toLowerCase().includes(q) ||
      staff.email?.toLowerCase().includes(q) ||
      staff.fullName?.toLowerCase().includes(q) ||
      staff.primaryRoleName?.toLowerCase().includes(q) ||
      staff.department?.name?.toLowerCase().includes(q);
    
    const matchesRole = !filterRole || staff.roleIds.includes(String(filterRole));
    const matchesStatus = filterStatus === '' || Number(staff.status) === Number(filterStatus);
    const matchesDepartment = !filterDepartment || String(staff.department?.id) === String(filterDepartment);
    
    return matchesSearch && matchesRole && matchesStatus && matchesDepartment;
  }), [staffMembers, searchText, filterRole, filterStatus, filterDepartment]);

  const hasActiveFilters = Boolean(searchText || filterRole || filterStatus !== '' || filterDepartment);

  useEffect(() => {
    setStaffPage(1);
  }, [searchText, filterRole, filterStatus, filterDepartment]);

  useEffect(() => {
    const pages = Math.max(1, Math.ceil(filteredData.length / staffPageSize));
    if (staffPage > pages) {
      setStaffPage(pages);
    }
  }, [filteredData.length, staffPageSize, staffPage]);

  const pagedData = useMemo(() => {
    const start = (staffPage - 1) * staffPageSize;
    return filteredData.slice(start, start + staffPageSize);
  }, [filteredData, staffPage, staffPageSize]);

  const heroMetrics = useMemo(() => {
    const total = staffMembers.length;
    const active = staffMembers.filter((s) => Number(s.status) === 1).length;
    const inactive = total - active;
    return [
      {
        key: 'total',
        title: 'Total staff',
        value: total,
        color: '#3497D3',
        icon: <TeamOutlined />,
        subtitle: 'All accounts',
      },
      {
        key: 'active',
        title: 'Active',
        value: active,
        color: '#49B870',
        icon: <CheckCircleOutlined />,
        subtitle: 'Enabled accounts',
      },
      {
        key: 'inactive',
        title: 'Inactive',
        value: inactive,
        color: '#94a3b8',
        icon: <CloseCircleOutlined />,
        subtitle: 'Disabled accounts',
      },
      {
        key: 'view',
        title: 'In this view',
        value: filteredData.length,
        color: '#722ed1',
        icon: <UnorderedListOutlined />,
        subtitle: hasActiveFilters ? 'Filtered results' : 'Showing all',
      },
    ].filter((m) => hasMetricValue(m.value) || m.key === 'view');
  }, [staffMembers, filteredData.length, hasActiveFilters]);

  const roleOptions = useMemo(() => (
    rbacRoles.map(role => ({
      value: String(role.id ?? role.role_id ?? role.uuid ?? role.name),
      label: role.display_name || role.name || role.key || `Role #${role.id ?? role.role_id}`,
      description: role.description,
    }))
  ), [rbacRoles]);

  const permissionOptions = useMemo(() => (
    permissions.map(permission => ({
      value: permission.id ?? permission.permission_id ?? permission.code,
      label: permission.display_name || permission.name || permission.code,
      resource: permission.resource || permission.group,
      description: permission.description,
    }))
  ), [permissions]);

  return (
    <div className="staff-dashboard">
      <div className="dashboard-page-stack">
        <header className="dash-masthead">
          <div className="dash-masthead__lead">
            <h1 className="dash-masthead__title">Staff Management</h1>
            <p className="dash-masthead__meta">
              Manage staff accounts, roles, and access permissions
            </p>
          </div>
        </header>

        <section className="staff-overview dash-overview dash-overview--compact" aria-label="Staff summary">
          <DashboardHero metrics={heroMetrics} loading={loading && !staffMembers.length} />
        </section>

        <DashboardSection
          id="staff-directory"
          icon={<TeamOutlined />}
          title="Staff directory"
          subtitle={`${filteredData.length} member${filteredData.length === 1 ? '' : 's'} in this view`}
          collapsible={false}
          variant="content"
          action={(
            <div className="staff-toolbar staff-toolbar--inline">
              <div className="staff-toolbar__filters">
                <label className="staff-toolbar__field staff-toolbar__field--search">
                  <span className="staff-toolbar__label">Search</span>
                  <AntInput
                    className="staff-toolbar__search"
                    size="small"
                    prefix={<SearchOutlined />}
                    placeholder="Staff ID, role, or department"
                    value={searchText}
                    onChange={(e) => setSearchText(e.target.value)}
                    allowClear
                  />
                </label>
                <label className="staff-toolbar__field">
                  <span className="staff-toolbar__label">Role</span>
                  <Select
                    size="small"
                    placeholder="All roles"
                    value={filterRole || undefined}
                    onChange={(value) => setFilterRole(value ?? '')}
                    allowClear
                    options={roleOptions}
                  />
                </label>
                <label className="staff-toolbar__field">
                  <span className="staff-toolbar__label">Department</span>
                  <Select
                    size="small"
                    placeholder="All departments"
                    value={filterDepartment || undefined}
                    onChange={(value) => setFilterDepartment(value ?? '')}
                    allowClear
                    options={departments}
                  />
                </label>
                <label className="staff-toolbar__field">
                  <span className="staff-toolbar__label">Status</span>
                  <Select
                    size="small"
                    placeholder="All statuses"
                    value={filterStatus !== '' ? filterStatus : undefined}
                    onChange={(value) => setFilterStatus(value ?? '')}
                    allowClear
                    options={[
                      { value: '1', label: 'Active' },
                      { value: '0', label: 'Inactive' },
                    ]}
                  />
                </label>
              </div>
              <div className="staff-toolbar__actions">
                <Button size="small" onClick={clearFilters} disabled={!hasActiveFilters}>
                  Clear
                </Button>
                <Button size="small" icon={<ReloadOutlined />} onClick={loadStaff} loading={loading}>
                  Refresh
                </Button>
                <Button type="primary" size="small" icon={<PlusOutlined />} onClick={() => setCreateModalVisible(true)}>
                  Add staff
                </Button>
              </div>
            </div>
          )}
        >
          {loading && !staffMembers.length ? (
            <div className="staff-loading">
              <Spin size="large" />
            </div>
          ) : filteredData.length === 0 ? (
            <Empty description="No staff members found" image={Empty.PRESENTED_IMAGE_SIMPLE} />
          ) : (
            <>
            <div className="staff-listing-stack">
              {pagedData.map((staff) => (
                <StaffListingCard
                  key={staff.id}
                  staff={staff}
                  loading={loading}
                  canManageRoles={canManageRoles}
                  onToggleStatus={handleToggleStatus}
                  onViewProfile={openProfileDrawer}
                  onManageRoles={openRoleModal}
                />
              ))}
            </div>
            <Pagination
              className="staff-listing-pagination"
              current={staffPage}
              pageSize={staffPageSize}
              total={filteredData.length}
              showSizeChanger
              pageSizeOptions={[10, 20, 50, 100]}
              showTotal={(total, range) => `${range[0]}–${range[1]} of ${total}`}
              hideOnSinglePage
              onChange={(page, pageSize) => {
                setStaffPage(pageSize !== staffPageSize ? 1 : page);
                setStaffPageSize(pageSize);
              }}
            />
            </>
          )}
        </DashboardSection>
      </div>

      <Modal
        title={
          <span>
            <UserOutlined /> Create New Staff Member
          </span>
        }
        open={createModalVisible}
        onOk={() => createForm.submit()}
        onCancel={() => {
          setCreateModalVisible(false);
          createForm.resetFields();
        }}
        destroyOnClose
        okText="Create Staff"
        cancelText="Cancel"
        confirmLoading={loading}
        width={500}
        rootClassName={ADMIN_REVIEW_MODAL_CLASS}
        className={`${ADMIN_REVIEW_MODAL_CLASS}__dialog`}
      >
        <Form
          form={createForm}
          layout="vertical"
          onFinish={handleCreateStaff}
          requiredMark={false}
        >
          <Form.Item
            name="email"
            label="Email"
            rules={[
              { required: true, message: 'Please enter an email' },
              { type: 'email', message: 'Enter a valid email address' },
            ]}
          >
            <Input prefix={<UserOutlined />} placeholder="name@amanhmo.com" />
          </Form.Item>

          <Form.Item
            name="full_name"
            label="Full name (optional)"
          >
            <Input placeholder="Display name" />
          </Form.Item>

          <Form.Item
            name="staffId"
            label="Staff ID (optional)"
            extra="Leave blank to auto-generate from the email local-part."
            rules={[
              { min: 3, message: 'Staff ID must be at least 3 characters' },
            ]}
          >
            <Input prefix={<UserOutlined />} placeholder="Optional Staff ID" />
          </Form.Item>
          
          <Form.Item
            name="password"
            label="Password"
            rules={[
              { required: true, message: 'Please enter a password' },
              { min: 8, message: 'Password must be at least 8 characters' }
            ]}
          >
            <Input.Password prefix={<LockOutlined />} placeholder="Enter password" />
          </Form.Item>
          
          <Form.Item
            name="departmentId"
            label="Department"
            rules={[{ required: true, message: 'Please select a department' }]}
          >
            <Select placeholder="Select Department">
              {departments.map(dept => (
                <Select.Option key={dept.value} value={dept.value}>
                  {dept.label}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
          
          <Form.Item
            name="rbacRoleId"
            label="Role"
            tooltip="Determines the account's permissions. The legacy compatibility role is derived from it automatically."
            rules={[{ required: true, message: 'Please select a role' }]}
          >
            <Select placeholder="Select Role" showSearch optionFilterProp="children">
              {roleOptions.map(role => (
                <Select.Option key={role.value} value={role.value}>
                  {role.label}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item
            name="send_invite"
            valuePropName="checked"
            initialValue={true}
          >
            <Checkbox>Email login details to this address</Checkbox>
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title={
          <span>
            Manage Roles – {selectedStaff?.staffId || selectedStaff?.fullName || 'Staff'}
          </span>
        }
        open={roleModalVisible}
        onCancel={closeRoleModal}
        footer={null}
        destroyOnClose
        width={520}
        rootClassName={ADMIN_REVIEW_MODAL_CLASS}
        className={`${ADMIN_REVIEW_MODAL_CLASS}__dialog`}
      >
        {!canManageRoles && (
          <Alert
            type="warning"
            showIcon
            message="You do not have permission to manage roles."
            className="staff-rbac-alert"
          />
        )}

        <Typography.Title level={5} style={{ marginBottom: 12 }}>
          Assigned Roles
        </Typography.Title>

        <List
          dataSource={roleAssignments}
          locale={{ emptyText: 'No roles assigned yet.' }}
          renderItem={(role) => {
            const roleId = role?.id ?? role?.role_id;
            const expiresAt = role?.expires_at || role?.expiresAt;
            return (
              <List.Item
                actions={
                  canManageRoles ? [
                    <Popconfirm
                      key="remove"
                      title="Remove role?"
                      description={`Remove ${role.display_name || role.name || 'role'} from this user.`}
                      onConfirm={() => handleRemoveRole(role)}
                      okButtonProps={{ loading: roleModalLoading }}
                      disabled={roleModalLoading}
                    >
                      <Button type="link" danger size="small">
                        Remove
                      </Button>
                    </Popconfirm>
                  ] : []
                }
              >
                <List.Item.Meta
                  title={role.display_name || role.name || role.role_name || `Role #${roleId}`}
                  description={role.description || role.role_description}
                />
                <Space direction="vertical" align="end">
                  {expiresAt && (
                    <Tag color="orange">
                      Expires {dayjs(expiresAt).format('DD MMM YYYY HH:mm')}
                    </Tag>
                  )}
                  {role.assigned_by && (
                    <Tag color="blue" style={{ marginTop: 4 }}>
                      Assigned by {role.assigned_by_name || role.assigned_by}
                    </Tag>
                  )}
                </Space>
              </List.Item>
            );
          }}
          style={{ marginBottom: 24 }}
        />

        {canManageRoles && (
          <>
            <Typography.Title level={5} style={{ marginBottom: 12 }}>
              Assign New Role
            </Typography.Title>
            <Form
              form={roleForm}
              layout="vertical"
              onFinish={handleAssignRole}
              disabled={roleModalLoading}
            >
              <Form.Item
                name="role_id"
                label="Role"
                rules={[{ required: true, message: 'Select a role to assign' }]}
              >
                <Select
                  showSearch
                  placeholder="Select role"
                  optionFilterProp="label"
                  options={roleOptions}
                />
              </Form.Item>
              <Form.Item
                name="expires_in_minutes"
                label="Expiration (optional)"
                initialValue={0}
                tooltip="Measured on the server clock. An expired assignment simply stops applying."
              >
                <Select
                  style={{ width: '100%' }}
                  disabled={roleModalLoading}
                  options={ROLE_EXPIRY_OPTIONS}
                />
              </Form.Item>
              <Form.Item name="context_note" label="Context note (optional)">
                <Input.TextArea rows={2} placeholder="e.g. Temporary access for audit" />
              </Form.Item>
              <Space style={{ width: '100%', justifyContent: 'space-between' }}>
                <Button type="primary" htmlType="submit" loading={roleModalLoading}>
                  Assign Role
                </Button>
                <Button type="link" onClick={() => setRoleCreationModalVisible(true)}>
                  Create New Role
                </Button>
              </Space>
            </Form>
          </>
        )}
      </Modal>

      <Modal
        title="Create New Role"
        open={roleCreationModalVisible}
        onCancel={() => {
          setRoleCreationModalVisible(false);
          newRoleForm.resetFields();
        }}
        footer={null}
        destroyOnClose
        width={520}
        rootClassName={ADMIN_REVIEW_MODAL_CLASS}
        className={`${ADMIN_REVIEW_MODAL_CLASS}__dialog`}
      >
        <Form layout="vertical" form={newRoleForm} onFinish={handleCreateRole}>
          <Form.Item
            label="Name"
            name="name"
            rules={[{ required: true, message: 'Role name is required' }]}
          >
            <Input placeholder="Internal code (e.g. claims_supervisor)" />
          </Form.Item>
          <Form.Item
            label="Display Name"
            name="display_name"
            rules={[{ required: true, message: 'Display name is required' }]}
          >
            <Input placeholder="Visible label (e.g. Claims Supervisor)" />
          </Form.Item>
          <Form.Item label="Description" name="description">
            <Input.TextArea rows={3} placeholder="Optional description" />
          </Form.Item>
          <Form.Item
            label="Department"
            name="department_id"
            tooltip="The department this role belongs to. Leave empty for a cross-department role."
          >
            <Select placeholder="Select department (optional)" allowClear>
              {departments.map(dept => (
                <Select.Option key={dept.value} value={dept.value}>
                  {dept.label}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
          <Form.Item label="Assign Permissions" name="permission_ids">
            <Select
              mode="multiple"
              allowClear
              placeholder="Select permissions to grant"
              optionFilterProp="label"
            >
              {permissionOptions.map(permission => (
                <Select.Option key={permission.value} value={permission.value}>
                  <Space direction="vertical" size={0}>
                    <span>{permission.label}</span>
                    <Text type="secondary" style={{ fontSize: 11 }}>
                      {permission.resource ? `${permission.resource} • ` : ''}{permission.description || 'No description'}
                    </Text>
                  </Space>
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
          <Space style={{ width: '100%', justifyContent: 'flex-end' }}>
            <Button onClick={() => {
              setRoleCreationModalVisible(false);
              newRoleForm.resetFields();
            }}>
              Cancel
            </Button>
            <Button type="primary" htmlType="submit" loading={roleModalLoading}>
              Create Role
            </Button>
          </Space>
        </Form>
      </Modal>

      <Drawer
        title={`Staff Profile – ${selectedProfile?.staffId || selectedProfile?.fullName || 'N/A'}`}
        open={profileDrawerVisible}
        onClose={closeProfileDrawer}
        width={420}
        destroyOnClose
        key={selectedProfile?.id || 'profile-drawer'}
        extra={
          selectedProfile && !profileEditing ? (
            <Button
              type="link"
              icon={<EditOutlined />}
              onClick={() => setProfileEditing(true)}
            >
              Edit
            </Button>
          ) : null
        }
      >
        {selectedProfile ? (
          <Space direction="vertical" size={16} style={{ width: '100%' }}>
            {profileEditing ? (
              <StaffProfileEditor
                initialValues={{
                  staffId: selectedProfile.staffId,
                  full_name: selectedProfile.fullName || selectedProfile.full_name || selectedProfile.name || '',
                  email: selectedProfile.email,
                  phone: selectedProfile.phone,
                }}
                submitting={profileSaving}
                onCancel={() => setProfileEditing(false)}
                onSubmit={handleSaveStaffProfile}
                passwordMode="admin"
              />
            ) : (
              <Descriptions column={1} bordered size="small">
                <Descriptions.Item label="Staff ID">{selectedProfile.staffId}</Descriptions.Item>
                <Descriptions.Item label="Full Name">{selectedProfile.fullName || '—'}</Descriptions.Item>
                <Descriptions.Item label="Email">{selectedProfile.email || '—'}</Descriptions.Item>
                <Descriptions.Item label="Phone">{selectedProfile.phone || '—'}</Descriptions.Item>
                <Descriptions.Item label="Department">{selectedProfile.department?.name || '—'}</Descriptions.Item>
                <Descriptions.Item label="Status">
                  <Tag color={Number(selectedProfile.status) === 1 ? 'success' : 'default'}>
                    {Number(selectedProfile.status) === 1 ? 'Active' : 'Inactive'}
                  </Tag>
                </Descriptions.Item>
              </Descriptions>
            )}

            <div>
              <Typography.Title level={5} style={{ marginBottom: 8 }}>Assigned Roles</Typography.Title>
              <Space wrap>
                {(selectedProfile.roles || []).map(role => (
                  <Tag key={role.id ?? role.role_id ?? role.name} color="geekblue">
                    {role.display_name || role.name || role.role_name || 'Role'}
                  </Tag>
                ))}
                {(!selectedProfile.roles || selectedProfile.roles.length === 0) && (
                  <Text type="secondary">No roles assigned yet.</Text>
                )}
              </Space>
            </div>

            <Divider style={{ margin: '12px 0' }} />
            {/* Direct grants sit between roles and effective permissions on
                purpose: they are the reason the effective list can exceed what
                the roles above would explain. */}
            <UserPermissionGrants
              userType="staff"
              userId={selectedProfile.id ?? selectedStaff?.id}
              canManage={canManageRoles}
            />

            <Divider style={{ margin: '12px 0' }} />
            <div>
              <Typography.Title level={5} style={{ marginBottom: 8 }}>Effective Permissions</Typography.Title>
              <Typography.Paragraph type="secondary" style={{ fontSize: 12 }}>
                Displaying unique permission codes aggregated from assigned roles.
              </Typography.Paragraph>
              <Space wrap>
                {(() => {
                  const perms = Array.from(new Set(
                    (selectedProfile.roles || []).flatMap(role => {
                      const rolePerms = role.permissions || role.permission_codes || role.permissionIds || [];
                      if (Array.isArray(rolePerms)) return rolePerms;
                      if (rolePerms && typeof rolePerms === 'object') {
                        return Object.keys(rolePerms).filter(key => rolePerms[key]);
                      }
                      return [];
                    })
                  ));
                  return perms.length ? perms.map(permission => (
                    <Tag key={typeof permission === 'object' ? permission.code || permission.id : permission} color="purple">
                      {typeof permission === 'object'
                        ? (permission.display_name || permission.code || permission.name || permission.id)
                        : permission}
                    </Tag>
                  )) : <Text type="secondary">No permissions available.</Text>;
                })()}
              </Space>
            </div>
          </Space>
        ) : (
          <Empty description="Select a staff member to view profile" />
        )}
      </Drawer>
    </div>
  );
};

export default StaffManagement;
